import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useState } from "react";
import { indicatorTransition } from "@/lib/motion";
import { cn } from "@/lib/utils";

function numericValue(text: string) {
  const digits = text.replace(/[^\d.-]/g, "");
  const value = Number(digits);
  return Number.isFinite(value) ? value : 0;
}

export function RollingText({
  text,
  animate = true,
  className,
}: {
  text: string;
  animate?: boolean;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const [snapshot, setSnapshot] = useState({ text, direction: 1 });
  if (text !== snapshot.text) {
    const direction = numericValue(text) >= numericValue(snapshot.text) ? 1 : -1;
    setSnapshot({ text, direction });
  }
  const direction = snapshot.direction;
  const characters = Array.from(text);
  const play = animate && !reduce;

  if (!play) {
    return <span className={cn("tabular-nums", className)}>{text}</span>;
  }

  return (
    <span className={cn("inline-flex items-center tabular-nums", className)} aria-hidden>
      {characters.map((character, index) => {
        const slot = characters.length - index;
        return (
          <CharacterColumn key={slot} character={character} direction={direction} />
        );
      })}
    </span>
  );
}

const digitVariants = {
  enter: (direction: number) => ({ y: direction > 0 ? "100%" : "-100%", opacity: 0.4 }),
  center: { y: "0%", opacity: 1 },
  exit: (direction: number) => ({ y: direction > 0 ? "-100%" : "100%", opacity: 0 }),
};

function CharacterColumn({ character, direction }: { character: string; direction: number }) {
  const digit = /\d/.test(character);
  if (!digit) {
    return <span className="inline-block whitespace-pre">{character}</span>;
  }

  return (
    <span className="relative inline-flex h-[1lh] w-[1ch] overflow-hidden">
      <AnimatePresence initial={false} custom={direction}>
        <motion.span
          key={character}
          custom={direction}
          variants={digitVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={indicatorTransition(false)}
          className="absolute inset-0 flex items-center justify-center"
        >
          {character}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}
